import type { WhiteLabelTheme } from '@/api/v1/public/white-label'
import { logger } from '@/lib/logger'

// Theme CSS imports
const themeModules: Record<
  WhiteLabelTheme,
  () => Promise<{ default: string }>
> = {
  Blue: () => import('@/styles/themes/blue.css?inline'),
  Green: () => import('@/styles/themes/green.css?inline'),
  Neutral: () => import('@/styles/themes/neutral.css?inline'),
  Orange: () => import('@/styles/themes/orange.css?inline'),
  Red: () => import('@/styles/themes/red.css?inline'),
  Rose: () => import('@/styles/themes/rose.css?inline'),
  Violet: () => import('@/styles/themes/violet.css?inline'),
  Yellow: () => import('@/styles/themes/yellow.css?inline'),
}

let currentStyleElement: HTMLStyleElement | null = null

/**
 * Applies a shadcn theme by dynamically loading and injecting the theme CSS
 * @param theme - The theme name (e.g., "Blue", "Green", etc.)
 */
export async function applyWhiteLabelTheme(theme: WhiteLabelTheme | null) {
  // Remove existing theme if no theme is provided or switching themes
  if (currentStyleElement) {
    currentStyleElement.remove()
    currentStyleElement = null
  }

  // No tenant theme → nothing to inject: the first-party Signater theme in
  // styles.css applies. Injecting a preset here would override it (this used
  // to force the Blue preset onto the first-party app).
  if (!theme) {
    logger.debug('[White Label] No tenant theme, using first-party theme')
    return
  }

  const themeToApply = theme

  try {
    logger.debug('[White Label] Applying theme', { theme: themeToApply })

    // Dynamically import the theme CSS
    const themeModule = await themeModules[themeToApply]()
    const themeCss = themeModule.default

    // Create a new style element and inject the theme CSS
    const styleElement = document.createElement('style')
    styleElement.id = 'white-label-theme'
    styleElement.textContent = themeCss
    document.head.appendChild(styleElement)

    currentStyleElement = styleElement

    logger.debug('[White Label] Theme applied successfully', {
      theme: themeToApply,
    })
  } catch (error) {
    logger.error(
      'Failed to apply white-label theme',
      error instanceof Error ? error : undefined,
      {
        theme: themeToApply,
      },
    )
  }
}

/**
 * Updates the favicon dynamically
 * @param faviconUrl - The URL of the favicon image
 */
export function updateFavicon(faviconUrl: string | null) {
  if (!faviconUrl) {
    return
  }

  // Remove existing favicon links
  const existingLinks = document.querySelectorAll("link[rel*='icon']")
  existingLinks.forEach((link) => link.remove())

  // Create and append new favicon link
  const link = document.createElement('link')
  link.rel = 'icon'
  link.type = 'image/png'
  link.href = faviconUrl
  document.head.appendChild(link)
}

/**
 * Updates the web app manifest dynamically
 * @param companyName - The company name
 * @param faviconUrl - The favicon URL (optional)
 */
export function updateManifest(companyName: string, faviconUrl: string | null) {
  if (typeof window === 'undefined') return

  try {
    // Create a dynamic manifest
    const manifest = {
      short_name: companyName,
      name: companyName,
      icons: faviconUrl
        ? [
            {
              src: faviconUrl,
              sizes: '192x192 512x512',
              type: 'image/png',
            },
          ]
        : [
            {
              src: '/favicon.ico',
              sizes: '64x64 32x32 24x24 16x16',
              type: 'image/x-icon',
            },
          ],
      start_url: '.',
      display: 'standalone',
      theme_color: '#000000',
      background_color: '#ffffff',
    }

    // Convert to JSON and create a blob
    const manifestBlob = new Blob([JSON.stringify(manifest)], {
      type: 'application/json',
    })
    const manifestURL = URL.createObjectURL(manifestBlob)

    // Remove existing manifest link
    const existingManifest = document.querySelector("link[rel='manifest']")
    if (existingManifest) {
      existingManifest.remove()
    }

    // Add new manifest link
    const manifestLink = document.createElement('link')
    manifestLink.rel = 'manifest'
    manifestLink.href = manifestURL
    document.head.appendChild(manifestLink)
  } catch (error) {
    logger.error(
      'Failed to update manifest',
      error instanceof Error ? error : undefined,
      {
        companyName,
        hasFavicon: !!faviconUrl,
      },
    )
  }
}
